import React from 'react';
import { useTranslation } from 'react-i18next';
import './field-rules.scss';

/**
 * pending → the value is not complete enough to judge yet (neutral)
 * valid   → rule satisfied
 * invalid → rule broken, the user has to change the value
 */
export type FieldRuleStatus = 'pending' | 'valid' | 'invalid';

export interface FieldRule {
    /** Translation key for the rule text. */
    key: string;
    status: FieldRuleStatus;
    /**
     * Rule that the submit-time validator does not enforce. Shown in amber
     * instead of red when broken, so a blocking error stays distinguishable
     * from a "please double-check this" hint.
     */
    advisory?: boolean;
}

interface FieldRulesProps {
    rules: FieldRule[];
    /** Hide the whole block until the user has typed something. */
    show: boolean;
}

const PendingMark = () => (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="5" fill="currentColor" />
    </svg>
);

const ValidMark = () => (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
        <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const InvalidMark = () => (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
    </svg>
);

/**
 * Compact live checklist rendered under a field while it is being filled in —
 * the same language as the password strength rules, without the meter bars.
 * Read-only: it mirrors the existing validators, it does not add validation.
 */
export const FieldRules: React.FC<FieldRulesProps> = ({ rules, show }) => {
    const { t } = useTranslation();

    if (!show) return null;

    return (
        <ul className="inv-field-rules" aria-live="polite">
            {rules.map((rule) => (
                <li
                    key={rule.key}
                    className={[
                        'inv-field-rules__rule',
                        `is-${rule.status}`,
                        rule.advisory ? 'is-advisory' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <span className="inv-field-rules__tick" aria-hidden="true">
                        {rule.status === 'valid' && <ValidMark />}
                        {rule.status === 'invalid' && <InvalidMark />}
                        {rule.status === 'pending' && <PendingMark />}
                    </span>
                    <span>{t(rule.key)}</span>
                </li>
            ))}
        </ul>
    );
};

export default FieldRules;
